import { FC, useEffect, useState } from 'react';
import {
    Grid,
    FormControl,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    Autocomplete,
    createFilterOptions,
    Typography,
    Box,
    InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIDateRanger, SCUIButton, SCUIAutoCompleteInput } from '../../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../../atomic/molecules';
import { OrderQuery } from '../../../../services/query';
import { initialState, addToCache, getRequests } from '../../Order.helper';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PaymentIcon from '@mui/icons-material/Payment';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { OOF } from './SCUIOrderFilter.classes';

interface TryOption {
    sellerId: number;
    storeName: string;
}
const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: TryOption) => option.storeName,
});
interface IProps {
    cargoNameList?: any;
    paymentList?: any;
    orderStatusList?: any;
    onClose?: any;
    orderInput: any;
    setOrderInput: any;
    setCachedSearchedValues: any;
}

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        fontSize: 13,
        transition: 'all 0.2s ease',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        fontSize: 13,
        fontWeight: 500,
    },
    '& .MuiInputAdornment-root svg': {
        fontSize: 18,
        color: 'primary.main',
    },
};

const selectSx = {
    borderRadius: 2,
    fontSize: 13,
    transition: 'all 0.2s ease',
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
    },
};

export const SCUIOrderFilter: FC<IProps> = ({
    orderInput,
    setOrderInput,
    cargoNameList,
    paymentList,
    orderStatusList,
    onClose,
    setCachedSearchedValues,
}) => {
    const { t } = useTranslation();
    const [selectedOrderStatus, setSelectedOrderStatus] = useState<string[]>([]);
    const [sellerName, setSellerName] = useState([]);
    const [orderInputForDrawerContent, setOrderInputForDrawerContent] = useState<any>(initialState.filterInfo);
    const _handlChangeDate = (event: any) => {
        setOrderInputForDrawerContent({
            ...orderInputForDrawerContent,
            startDate: event[0],
            endDate: event[1],
        });
    };
    const clearForm = () => {
        setSelectedOrderStatus([]);
        setOrderInputForDrawerContent(initialState.filterInfo);
        const form = document.querySelector('form');
        if (form) form.reset();
    };
    const GetAllSellerName = async () => {
        try {
            let res: any = await OrderQuery.GetSellerNameList();

            if (res?.data) {
                setSellerName(res.data);
            }
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };

    useEffect(() => {
        setSelectedOrderStatus(
            orderStatusList.filter((order: any) =>
                orderInput.orderStatusIds.split(',').map(Number).includes(order.orderStatusId)
            )
        );
        GetAllSellerName();
    }, []);

    const _handlChangeTag = (_: any, newValue: any) => {
        setSelectedOrderStatus(newValue);
        setOrderInputForDrawerContent({
            ...orderInputForDrawerContent,
            orderStatusIds: newValue.map(function (item: any) {
                return item.orderStatusId;
            }),
        });
    };

    return (
        <form
            className={OOF.form}
            onSubmit={async (e: any) => {
                e.preventDefault();
                setOrderInput(orderInputForDrawerContent);
                let params = {
                    ...orderInputForDrawerContent,
                    Barcodes: Array.isArray(orderInputForDrawerContent.Barcodes)
                        ? orderInputForDrawerContent.Barcodes
                        : typeof orderInputForDrawerContent.Barcodes === 'string' && orderInputForDrawerContent.Barcodes
                          ? orderInputForDrawerContent.Barcodes.split(',')
                          : [],
                    orderStatusIds: Array.isArray(orderInputForDrawerContent.orderStatusIds)
                        ? orderInputForDrawerContent.orderStatusIds
                        : typeof orderInputForDrawerContent.orderStatusIds === 'string' &&
                            orderInputForDrawerContent.orderStatusIds
                          ? orderInputForDrawerContent.orderStatusIds.split(',')
                          : [],
                };
                addToCache(params);

                const cachedOrders = await getRequests();
                if (cachedOrders.length <= 10) {
                    setCachedSearchedValues(cachedOrders.reverse());
                } else {
                    setCachedSearchedValues(cachedOrders.slice(Math.max(cachedOrders.length - 10, 1)).reverse());
                }
            }}
        >
            <Box className={OOF.gridRoot} sx={{ px: 2.5, py: 2 }}>
                <Grid container spacing={0}>
                    <SCUIGeneralDrawerHeader
                        header={t('general.detailedSearch')}
                        onClose={onClose}
                        rootClassName={OOF.headerRoot}
                        titleClassName={OOF.headerTitle}
                        closeIconClassName={OOF.headerCloseIcon}
                    />

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className={OOF.inputOrderNumber}
                            defaultValue={orderInput.orderNumber}
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    orderNumber: e.target.value.trim(),
                                })
                            }
                            label={t('general.orderNo')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ReceiptLongIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className={OOF.inputCustomerPhone}
                            defaultValue={orderInput.PhoneNumberbyCustomerId}
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    PhoneNumberbyCustomerId: e.target.value.trim(),
                                })
                            }
                            label={t('general.customerPhoneNumber')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PhoneAndroidIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className={OOF.inputDeliveryPhone}
                            defaultValue={orderInput.PhoneNumberbyDelivery}
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    PhoneNumberbyDelivery: e.target.value.trim(),
                                })
                            }
                            label={t('general.shippingPhoneNumber')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocalShippingIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>

                    <Grid
                        size={12}
                        sx={{
                            mb: 2,
                            '& .MuiInputAdornment-root': {
                                marginRight: '2px !important',
                                marginLeft: '8px !important',
                            },
                        }}
                    >
                        <Autocomplete
                            className={OOF.sellerAutocompleteWrap}
                            disablePortal
                            id="combo-box-demo"
                            options={sellerName}
                            getOptionLabel={(option) => option.storeName}
                            filterOptions={filterOptions}
                            onChange={(event: any, newValue: any) => {
                                if (newValue) {
                                    setOrderInputForDrawerContent({
                                        ...orderInputForDrawerContent,
                                        sellerName: newValue['storeName'].trim(),
                                    });
                                } else {
                                    setOrderInputForDrawerContent({
                                        ...orderInputForDrawerContent,
                                        sellerName: '',
                                    });
                                }
                            }}
                            size="small"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('general.sellerName')}
                                    sx={textFieldSx}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <StorefrontIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className={OOF.inputShippingListNo}
                            defaultValue={orderInput.shippingListNo}
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    shippingListNo: e.target.value.trim(),
                                })
                            }
                            label={t('general.shippingNo')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ListAltIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className={OOF.inputDPackage}
                            defaultValue={orderInput.DPackageCargoShipmentCode}
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    DPackageCargoShipmentCode: e.target.value.trim(),
                                    IsDKoli: e.target.value.trim() !== '',
                                })
                            }
                            label={t('general.dPackageNo')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Inventory2Icon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className={OOF.inputTrackingCode}
                            defaultValue={orderInput.TrackingCode}
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    TrackingCode: e.target.value.trim(),
                                })
                            }
                            label={t('general.mpCargoShipmentNo')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <GpsFixedIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 0.5 }}>
                        <TextField
                            className={OOF.inputBarcodes}
                            defaultValue={orderInput.barcode}
                            label={t('searchService.barcode')}
                            type="text"
                            onChange={(e) =>
                                setOrderInputForDrawerContent({
                                    ...orderInputForDrawerContent,
                                    Barcodes: e.target.value.trim(),
                                })
                            }
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <QrCodeScannerIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={12} sx={{ mb: 2 }}>
                        <Typography
                            variant="caption"
                            display="block"
                            sx={{
                                color: 'warning.dark',
                                fontSize: 11,
                                fontWeight: 500,
                                mt: 0.5,
                                pl: 0.5,
                            }}
                        >
                            {t('order.barcodeSearchHint')}
                        </Typography>
                    </Grid>
                    <Grid size={12} sx={{ mb: 2 }}>
                        <FormControl className={OOF.formControlPaymentMethod} fullWidth size="small">
                            <InputLabel id="paymentMethod">{t('general.paymentMethod')}</InputLabel>
                            <Select
                                className={OOF.selectPaymentMethod}
                                value={orderInputForDrawerContent.paymentMethodId || '0'}
                                fullWidth
                                label={t('general.paymentMethod')}
                                id="paymentMethod"
                                sx={selectSx}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <PaymentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    </InputAdornment>
                                }
                                onChange={(e) =>
                                    setOrderInputForDrawerContent({
                                        ...orderInputForDrawerContent,
                                        paymentMethodId: Number(e.target.value),
                                    })
                                }
                            >
                                {paymentList &&
                                    paymentList.map((paymentMethod: any, index: number) => (
                                        <MenuItem
                                            key={index}
                                            id={paymentMethod.paymentMethodId}
                                            value={paymentMethod.paymentMethodId}
                                        >
                                            {paymentMethod.paymentMethodName}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                        <Grid size={12} sx={{ mb: 2, '& .MuiAutocomplete-inputRoot': { padding: '2px !important' } }}>
                            <div className={OOF.orderStatusAutocompleteWrap}>
                                <SCUIAutoCompleteInput
                                    list={orderStatusList}
                                    onChange={_handlChangeTag}
                                    multiple={true}
                                    defValue={selectedOrderStatus}
                                    txtPlaceHolder={'Statü'}
                                    searchValue="backendDescriptionKey"
                                />
                            </div>
                        </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <FormControl className={OOF.formControlCargoCompany} fullWidth size="small">
                            <InputLabel id="cargoCompanyLabel">{t('general.cargoCompany')}</InputLabel>
                            <Select
                                className={OOF.selectCargoCompany}
                                fullWidth
                                label={t('general.cargoCompany')}
                                id="parentMenuLabel"
                                value={orderInputForDrawerContent.shippingCompanyId || '0'}
                                sx={selectSx}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <LocalShippingIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    </InputAdornment>
                                }
                                onChange={(e) => {
                                    setOrderInputForDrawerContent({
                                        ...orderInputForDrawerContent,
                                        shippingCompanyId: Number(e.target.value),
                                    });
                                }}
                            >
                                {cargoNameList &&
                                    cargoNameList.map((cargo: any) => (
                                        <MenuItem key={cargo.id} id={cargo.id} value={cargo.id}>
                                            {cargo.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={12} sx={{ mb: 3 }}>
                        <div className={OOF.dateRangerWrap}>
                            <SCUIDateRanger setDateRange={_handlChangeDate} dateFormat="YYYY-MM-DD" />
                        </div>
                    </Grid>

                    <Grid size={12}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: 'background.paper',
                                pt: 1,
                                pb: 0.5,
                            }}
                        >
                            <SCUIButton
                                className={OOF.clearButton}
                                text={t('general.clear')}
                                onClick={clearForm}
                                type="primary"
                                buttonType="button"
                                sizeType="fixed"
                                style={{
                                    backgroundColor: '#ef4444',
                                    borderColor: '#ef4444',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                                size={'m'}
                                fullwidth
                                showStartIcon
                                startIcon={<RestartAltIcon style={{ fontSize: '16px' }} />}
                            />
                            <SCUIButton
                                className={OOF.searchButton}
                                text={t('general.list')}
                                type="primary"
                                sizeType="fixed"
                                buttonType="submit"
                                style={{
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                                size={'m'}
                                fullwidth
                                showStartIcon
                                startIcon={<SearchIcon style={{ fontSize: '16px' }} />}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </form>
    );
};
